<template>
  <div class="timeline-view">
    <div v-if="loading" class="text-center py-16">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <p class="mt-4 text-grey">Loading timeline...</p>
    </div>

    <template v-else>
      <!-- Toolbar -->
      <div class="tl-toolbar">
        <div class="tl-zoom-group">
          <button
            v-for="(z, i) in ZOOM_LEVELS"
            :key="i"
            class="zoom-btn"
            :class="{ active: zoomIdx === i }"
            @click="zoomIdx = i"
          >{{ z.label }}</button>
        </div>

        <div class="tl-toolbar-actions">
          <v-btn size="default" variant="tonal" rounded="pill" @click="scrollToNow">
            <v-icon start size="16">mdi-clock-fast</v-icon>
            Now
          </v-btn>
          <v-btn size="default" variant="tonal" rounded="pill" @click="openDatePicker">
            <v-icon start size="16">mdi-calendar-search</v-icon>
            Jump to date
          </v-btn>
          <v-btn
            size="default"
            rounded="pill"
            :variant="isFocusMode ? 'flat' : 'tonal'"
            :color="isFocusMode ? 'primary' : undefined"
            @click="isFocusMode = !isFocusMode"
          >
            <v-icon start size="16">{{ isFocusMode ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}</v-icon>
            {{ isFocusMode ? 'Exit full width' : 'Full width' }}
          </v-btn>
        </div>
      </div>

      <!-- Date jump dialog -->
      <v-dialog v-model="showDatePicker" max-width="300">
        <v-card rounded="xl" class="pa-4" style="background:#1a1a1a; border:1px solid rgba(255,255,255,0.08)">
          <div class="text-subtitle-2 mb-3 font-weight-bold" style="color:#fff">Jump to date &amp; time</div>
          <v-text-field
            v-model="jumpDateStr"
            type="datetime-local"
            variant="outlined"
            density="compact"
            color="primary"
            hide-details
          />
          <div class="d-flex ga-2 mt-3 justify-end">
            <v-btn size="small" variant="text" color="grey" @click="showDatePicker = false">Cancel</v-btn>
            <v-btn size="small" color="primary" variant="tonal" rounded="pill" @click="doJump">Jump</v-btn>
          </div>
        </v-card>
      </v-dialog>

      <!-- Timeline strip -->
      <div
        class="tl-strip"
        ref="scrollWrapper"
        @scroll.passive="onScroll"
        @wheel.prevent="handleWheel"
        @mousedown="onMouseDown"
        @mousemove="onMouseMove"
        @mouseup="onMouseUp"
        @mouseleave="onMouseUp"
      >
        <div
          class="tl-track"
          :style="{ width: trackWidth + 'px', height: trackHeight + 'px' }"
        >
          <!-- Day separator lines (behind everything) -->
          <div
            v-for="sep in daySeparators"
            :key="sep.t"
            class="tl-day-sep"
            :style="{ left: sep.x + 'px', height: rulerTop + 'px' }"
          ></div>

          <!-- Hovered post connector line -->
          <div
            v-if="hoveredItem"
            class="tl-hover-connector"
            :style="{
              left: hoveredItem.x + 'px',
              top: (hoveredItem.y + CARD_H) + 'px',
              height: (rulerTop - hoveredItem.y - CARD_H) + 'px',
            }"
          ></div>

          <!-- Floating post bubbles -->
          <div
            v-for="item in orderedPositionedItems"
            :key="getUri(item)"
            class="tl-bubble"
            :class="{ 'bubble-active': expandedUri === getUri(item) }"
            :style="{ left: item.x + 'px', top: item.y + 'px' }"
            @click="toggleExpand(item)"
            @mouseenter="hoveredItem = item"
            @mouseleave="hoveredItem = null"
          >
            <div class="bubble-header">
              <v-avatar size="18" class="mr-2 flex-shrink-0">
                <v-img v-if="getPost(item).author?.avatar" :src="getPost(item).author.avatar"></v-img>
                <v-icon v-else icon="mdi-account" size="11"></v-icon>
              </v-avatar>
              <div class="bubble-name">{{ getPost(item).author?.displayName || getPost(item).author?.handle }}</div>
            </div>
            <div class="bubble-text">{{ getPost(item).record?.text || '' }}</div>
          </div>

          <!-- Time ruler -->
          <div class="tl-ruler" :style="{ top: rulerTop + 'px' }">
            <div class="tl-now-line" style="left: 0px">
              <div class="now-badge">now</div>
            </div>

            <div
              v-for="mark in timeMarks"
              :key="mark.t"
              class="tl-mark"
              :class="{ 'mark-major': mark.isMajor, 'mark-day': mark.isDay }"
              :style="{ left: mark.x + 'px' }"
              @click="scrollToX(mark.x)"
            >
              <div class="mark-tick"></div>
              <div v-if="mark.label" class="mark-label">{{ mark.label }}</div>
            </div>

            <!-- Hover time indicator -->
            <div
              v-if="hoveredItem"
              class="tl-hover-mark"
              :style="{ left: hoveredItem.x + 'px' }"
            >
              <div class="hover-label">{{ formatDateTime(getPost(hoveredItem).indexedAt) }}</div>
            </div>
          </div>

          <!-- Lazy-load spinner at the far right edge -->
          <div
            v-if="anyLoading && posts.length > 0"
            class="tl-load-spinner"
            :style="{ left: (trackWidth - 36) + 'px', top: (rulerTop + 11) + 'px' }"
          >
            <v-progress-circular indeterminate size="18" width="2" color="primary" />
          </div>
        </div>
      </div>

      <!-- Scroll hint + feed toggle -->
      <div class="scroll-hint">
        <v-icon icon="mdi-clock-outline" size="13" color="grey"></v-icon>
        <span>left = now · scroll right for older posts · drag or scroll to navigate</span>

        <v-menu
          v-model="showFeedMenu"
          location="top end"
          :close-on-content-click="false"
          :offset="8"
        >
          <template #activator="{ props }">
            <button v-bind="props" class="loaded-posts-btn">
              <v-icon size="11" class="mr-1">mdi-tune-variant</v-icon>
              {{ sources.filter(s => s.enabled).length }} loaded feeds
            </button>
          </template>

          <div class="feed-popup">
            <div class="feed-popup-title">Feed sources</div>
            <div class="feed-popup-list">
              <!-- Hashtag sources -->
              <div class="feed-popup-section-label">Hashtags</div>
              <label
                v-for="s in sources.filter(s => s.kind === 'tag')"
                :key="s.id"
                class="feed-popup-item"
                @click.prevent="toggleSource(s)"
              >
                <div class="feed-checkbox" :class="{ checked: s.enabled }">
                  <v-icon v-if="s.enabled" size="11" color="primary">mdi-check</v-icon>
                </div>
                <div class="feed-popup-label">
                  <span class="feed-name">{{ s.label }}</span>
                  <span class="feed-meta">
                    <template v-if="s.loading && s.enabled">loading…</template>
                    <template v-else-if="s.error">error</template>
                    <template v-else-if="!s.enabled">off</template>
                    <template v-else>{{ postCountBySource[s.id] ?? 0 }} posts</template>
                  </span>
                </div>
              </label>

              <!-- Custom discovers -->
              <template v-if="sources.some(s => s.kind === 'discover')">
                <div class="feed-popup-section-label mt-2">My Discoveries</div>
                <label
                  v-for="s in sources.filter(s => s.kind === 'discover')"
                  :key="s.id"
                  class="feed-popup-item"
                  @click.prevent="toggleSource(s)"
                >
                  <div class="feed-checkbox" :class="{ checked: s.enabled }">
                    <v-icon v-if="s.enabled" size="11" color="primary">mdi-check</v-icon>
                  </div>
                  <div class="feed-popup-label">
                    <span class="feed-name">{{ s.label }}</span>
                    <span class="feed-meta">
                      <template v-if="s.loading && s.enabled">loading…</template>
                      <template v-else-if="s.error">error</template>
                      <template v-else-if="!s.enabled">off</template>
                      <template v-else>{{ postCountBySource[s.id] ?? 0 }} posts</template>
                    </span>
                  </div>
                </label>
              </template>
            </div>
            <div class="feed-popup-actions">
              <button class="feed-popup-action-btn" @click="setAllSources(true)">All on</button>
              <button class="feed-popup-action-btn" @click="setAllSources(false)">All off</button>
            </div>
          </div>
        </v-menu>
      </div>

      <!-- Expanded Post Panel -->
      <transition name="slide-up">
        <div v-if="expandedItem" ref="expandedPanel" class="expanded-panel-wrap mt-3">
        <div class="expanded-panel">
          <div class="d-flex align-center justify-space-between mb-3 px-1">
            <div class="d-flex align-center ga-2">
              <v-avatar size="28">
                <v-img v-if="getPost(expandedItem).author?.avatar" :src="getPost(expandedItem).author.avatar"></v-img>
                <v-icon v-else icon="mdi-account" size="18"></v-icon>
              </v-avatar>
              <span class="text-body-2 font-weight-black text-white">
                {{ getPost(expandedItem).author?.displayName || getPost(expandedItem).author?.handle }}
              </span>
            </div>
            <v-btn icon="mdi-close" variant="text" size="small" color="grey" @click="closeExpanded"></v-btn>
          </div>
          <PostItem
            :post="expandedItem.post || expandedItem"
            :reply="expandedItem.reply"
            @view-thread="viewThread"
            @reply="viewThread"
            @delete="handleDelete"
            @tag-click="handleTagClick"
            @view-profile="handleViewProfile"
          />
          <div class="mt-4 d-flex justify-center">
            <v-btn color="primary" variant="tonal" rounded="pill" @click="viewThread(expandedItem.post || expandedItem)">
              <v-icon start icon="mdi-comment-text-outline"></v-icon>
              Open Thread
            </v-btn>
          </div>
        </div>
        </div>
      </transition>
    </template>

    <v-alert v-if="!loading && visiblePosts.length === 0 && !anyLoading" type="warning" variant="tonal" class="rounded-xl">
      No posts found.
    </v-alert>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import { searchPosts, getDiscovers, currentUser, isFocusMode } from '@/services/bluesky';
import PostItem from '@/components/PostItem.vue';

const router = useRouter();

// ── Zoom levels ────────────────────────────────────────────
const ZOOM_LEVELS: { label: string; pxPerMin: number; stepMin: number; unit?: 'day' | 'week' }[] = [
  { label: '1d', pxPerMin: 0.35,  stepMin: 1440, unit: 'day'  },
  { label: '1w', pxPerMin: 0.065, stepMin: 10080, unit: 'week' },
];

// ── Layout constants ───────────────────────────────────────
const CARD_W      = 148;
const CARD_H      = 62;   // compact (2-line text)
const LANE_H      = CARD_H + 10;
const LANE_PAD    = 6;
const RULER_H     = 40;
const TOP_PAD     = 12;
const STRIP_H     = 400;  // fixed strip height
const MAX_LANES   = Math.max(1, Math.floor((STRIP_H - RULER_H - TOP_PAD - LANE_PAD) / LANE_H));

// ── Built-in #gdi* hashtag sources ────────────────────────
const GDI_TAGS = [
  { id: 'gamedevid',  label: '#gamedevid',  query: '#gamedevid'  },
  { id: 'gdiprog',    label: '#gdiprog',    query: '#gdiprog'    },
  { id: 'gdiengine',  label: '#gdiengine',  query: '#gdiengine'  },
  { id: 'gdiart',     label: '#gdiart',     query: '#gdiart'     },
  { id: 'gdidesign',  label: '#gdidesign',  query: '#gdidesign'  },
  { id: 'gdidevlog',  label: '#gdidevlog',  query: '#gdidevlog'  },
  { id: 'gdirelease', label: '#gdirelease', query: '#gdirelease' },
  { id: 'gdicollab',  label: '#gdicollab',  query: '#gdicollab'  },
  { id: 'gdihelp',    label: '#gdihelp',    query: '#gdihelp'    },
  { id: 'gdijob',     label: '#gdijob',     query: '#gdijob'     },
  { id: 'gdinote',    label: '#gdinote',    query: '#gdinote'    },
];

// ── LocalStorage helpers for persisting enabled sources ────
// null = never set before → default to only gamedevid
const LS_KEY = 'tl-enabled-sources';
const loadEnabled = (): Set<string> | null => {
  const raw = localStorage.getItem(LS_KEY);
  if (raw === null) return null;
  try { return new Set(JSON.parse(raw)); }
  catch { return null; }
};
const saveEnabled = (ids: string[]) => {
  localStorage.setItem(LS_KEY, JSON.stringify(ids));
};

// ── Source type ────────────────────────────────────────────
interface Source {
  id: string;
  label: string;
  query: string;
  kind: 'tag' | 'discover';
  cursor?: string;
  done: boolean;
  loading: boolean;
  error: boolean;
  enabled: boolean;
}

// ── State ──────────────────────────────────────────────────
const posts         = ref<any[]>([]);
const loading       = ref(true);
const expandedUri   = ref<string | null>(null);
const expandedItem  = ref<any>(null);
const scrollWrapper = ref<HTMLElement | null>(null);
const expandedPanel = ref<HTMLElement | null>(null);
const sources       = ref<Source[]>([]);
const seenUris      = new Set<string>();
const showFeedMenu  = ref(false);
const hoveredItem   = ref<any>(null);

const zoomIdx        = ref(0);
const showDatePicker = ref(false);
const jumpDateStr    = ref('');
const referenceMs    = ref(Date.now());

let isDragging = false, startX = 0, scrollLeftStart = 0, hasDragged = false;

// ── Derived zoom ───────────────────────────────────────────
const pxPerMin = computed(() => ZOOM_LEVELS[zoomIdx.value].pxPerMin);

// ── Computed loading state ─────────────────────────────────
const anyLoading = computed(() => sources.value.some(s => s.loading && s.enabled));

// ── Visible posts (filtered by enabled sources) ────────────
const visiblePosts = computed(() => {
  const enabledIds = new Set(sources.value.filter(s => s.enabled).map(s => s.id));
  return posts.value.filter(p => !p._sourceId || enabledIds.has(p._sourceId));
});

// ── Post count per source ──────────────────────────────────
const postCountBySource = computed(() => {
  const counts: Record<string, number> = {};
  for (const p of posts.value) {
    const sid = p._sourceId;
    if (sid) counts[sid] = (counts[sid] ?? 0) + 1;
  }
  return counts;
});

// ── Helpers ────────────────────────────────────────────────
const getPost  = (item: any) => item.post || item;
const getUri   = (item: any) => item.post?.uri || item.uri;
const postTime = (item: any) => new Date(getPost(item).indexedAt).getTime();

const formatDateTime = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// ── Coordinate: x=0 is "now", larger x = further back in time ─
const toX = (ms: number) => ((referenceMs.value - ms) / 60_000) * pxPerMin.value;

// ── Time range ─────────────────────────────────────────────
const oldestMs = computed(() => {
  if (!visiblePosts.value.length) return referenceMs.value - 3_600_000;
  return Math.min(...visiblePosts.value.map(postTime));
});

const padMins = computed(() => {
  const u = ZOOM_LEVELS[zoomIdx.value].unit;
  if (u === 'week') return 7 * 24 * 60;
  if (u === 'day')  return 24 * 60;
  return 60;
});

const trackWidth = computed(() =>
  Math.round(((referenceMs.value - oldestMs.value) / 60_000 + padMins.value) * pxPerMin.value)
);

// ── Lane assignment ────────────────────────────────────────
const positionedItems = computed(() => {
  const slotPx = Math.round(CARD_W * 0.55); // allow posts to be close but not fully overlapping
  const sorted = [...visiblePosts.value].sort((a, b) => postTime(b) - postTime(a));
  const laneRightEdge: number[] = [];

  const withLanes = sorted.map(item => {
    const x = Math.round(toX(postTime(item)));
    let lane = laneRightEdge.findIndex(edge => x >= edge);
    if (lane === -1) {
      if (laneRightEdge.length < MAX_LANES) {
        lane = laneRightEdge.length;
        laneRightEdge.push(0);
      } else {
        // All lanes full — reuse the least-blocked lane (allows overlap)
        lane = laneRightEdge.indexOf(Math.min(...laneRightEdge));
      }
    }
    laneRightEdge[lane] = x + slotPx;
    return { ...item, x, lane };
  });

  return withLanes.map(item => ({
    ...item,
    y: TOP_PAD + (MAX_LANES - 1 - item.lane) * LANE_H + LANE_PAD,
  }));
});

const rulerTop    = STRIP_H - RULER_H;
const trackHeight = STRIP_H;

// ── DOM-ordered items: hovered/active rendered last so they paint on top ──
const orderedPositionedItems = computed(() => {
  const activeUri  = expandedUri.value;
  const hovUri     = hoveredItem.value ? getUri(hoveredItem.value) : null;
  return [...positionedItems.value].sort((a, b) => {
    const aUri = getUri(a), bUri = getUri(b);
    const aScore = aUri === hovUri ? 2 : aUri === activeUri ? 1 : 0;
    const bScore = bUri === hovUri ? 2 : bUri === activeUri ? 1 : 0;
    return aScore - bScore;
  });
});

// ── Day separator lines (dashed verticals) ─────────────────
const daySeparators = computed(() => {
  const seps: { t: number; x: number }[] = [];
  const ref = new Date(referenceMs.value);
  // Start at yesterday midnight (today midnight would be x=~0 which overlaps "now")
  let d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  while (d.getTime() >= oldestMs.value - 86_400_000) {
    const t = d.getTime();
    const x = toX(t);
    if (x > 4 && x <= trackWidth.value + 20) {
      seps.push({ t, x });
    }
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
  }
  return seps;
});

// ── Time marks ─────────────────────────────────────────────
const timeMarks = computed(() => {
  const marks: { t: number; x: number; label: string; isMajor: boolean; isDay: boolean }[] = [];
  const zoom = ZOOM_LEVELS[zoomIdx.value];
  const limitMs = oldestMs.value;

  if (zoom.unit === 'day') {
    const ref = new Date(referenceMs.value);
    let d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate() + 1);
    while (d.getTime() >= limitMs - 86_400_000) {
      const t = d.getTime();
      const x = toX(t);
      if (x >= -20 && x <= trackWidth.value + 100) {
        const label = d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
        marks.push({ t, x, label, isMajor: true, isDay: true });
      }
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
    }
    return marks;
  }

  if (zoom.unit === 'week') {
    const ref = new Date(referenceMs.value);
    const dayOfWeek = ref.getDay();
    const daysToNextMon = dayOfWeek === 1 ? 7 : (8 - dayOfWeek) % 7 || 7;
    let d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate() + daysToNextMon);
    while (d.getTime() >= limitMs - 7 * 86_400_000) {
      const t = d.getTime();
      const x = toX(t);
      if (x >= -20 && x <= trackWidth.value + 100) {
        marks.push({ t, x, label: d.toLocaleDateString([], { month: 'short', day: 'numeric' }), isMajor: true, isDay: true });
      }
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 7);
    }
    return marks;
  }

  const stepMs = zoom.stepMin * 60_000;
  const snapStart = Math.ceil(referenceMs.value / stepMs) * stepMs;
  for (let t = snapStart; t >= limitMs - stepMs; t -= stepMs) {
    const x = toX(t);
    if (x < -20 || x > trackWidth.value + 60) continue;
    const d = new Date(t);
    const isDay  = d.getMinutes() === 0 && d.getHours() === 0;
    const isHour = d.getMinutes() === 0;
    let label = '', isMajor = false;
    if (isDay) {
      label = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
      isMajor = true;
    } else {
      label = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      isMajor = isHour;
    }
    marks.push({ t, x, label, isMajor, isDay });
  }
  return marks;
});

// ── Scroll helpers ─────────────────────────────────────────
const scrollToX = (x: number) => {
  scrollWrapper.value?.scrollTo({ left: x - (scrollWrapper.value.clientWidth / 2), behavior: 'smooth' });
};
const scrollToNow = () => {
  scrollWrapper.value?.scrollTo({ left: 0, behavior: 'smooth' });
};

// ── Date jump ──────────────────────────────────────────────
const openDatePicker = () => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  jumpDateStr.value = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  showDatePicker.value = true;
};
const doJump = () => {
  showDatePicker.value = false;
  if (!jumpDateStr.value) return;
  const ms = new Date(jumpDateStr.value).getTime();
  if (isNaN(ms)) return;
  const x = toX(ms);
  scrollWrapper.value?.scrollTo({ left: Math.max(0, x - (scrollWrapper.value.clientWidth / 2)), behavior: 'smooth' });
};

// ── Multi-source fetch ─────────────────────────────────────
const addPosts = (results: any[], sourceId: string) => {
  const fresh = results.filter(p => {
    const uri = getUri(p);
    if (seenUris.has(uri)) return false;
    seenUris.add(uri);
    return true;
  });
  if (fresh.length) posts.value = [...posts.value, ...fresh.map(p => ({ ...p, _sourceId: sourceId }))];
};

const fetchSource = async (source: Source) => {
  if (!source.enabled || source.loading || source.done) return;
  source.loading = true;
  source.error = false;
  try {
    const { posts: results, cursor: next } = await searchPosts(source.query, 30, source.cursor);
    addPosts(results, source.id);
    source.cursor = next;
    source.done = !next;
  } catch {
    source.error = true;
    source.done = true;
  } finally {
    source.loading = false;
  }
};

const buildSources = async (): Promise<Source[]> => {
  const enabled = loadEnabled() ?? new Set(['gamedevid']);
  const srcs: Source[] = GDI_TAGS.map(t => ({
    ...t,
    kind: 'tag' as const,
    done: false,
    loading: false,
    error: false,
    enabled: enabled.has(t.id),
  }));

  if (currentUser.value?.did) {
    try {
      const discovers = await getDiscovers();
      for (const d of discovers) {
        const id = `discover-${d.rkey || d.name}`;
        srcs.push({
          id,
          label: d.name,
          query: d.query,
          kind: 'discover' as const,
          done: false,
          loading: false,
          error: false,
          enabled: enabled.has(id),
        });
      }
    } catch { /* user discovers optional */ }
  }
  return srcs;
};

// Lazy-load: fetch next page for every non-done enabled source concurrently
const loadMoreAll = () => {
  const pending = sources.value.filter(s => s.enabled && !s.done && !s.loading);
  if (!pending.length) return;
  pending.forEach(s => fetchSource(s)); // intentionally not awaited
};

// ── Source toggle ──────────────────────────────────────────
const toggleSource = (source: Source) => {
  source.enabled = !source.enabled;
  // If turning on and not yet fetched, kick off fetch
  if (source.enabled && !source.done && !source.loading) {
    fetchSource(source);
  }
  saveEnabled(sources.value.filter(s => s.enabled).map(s => s.id));
};

const setAllSources = (on: boolean) => {
  for (const s of sources.value) {
    if (on && !s.enabled) {
      s.enabled = true;
      if (!s.done && !s.loading) fetchSource(s);
    } else if (!on) {
      s.enabled = false;
    }
  }
  saveEnabled(sources.value.filter(s => s.enabled).map(s => s.id));
};

// ── Scroll → lazy load ─────────────────────────────────────
const onScroll = () => {
  const el = scrollWrapper.value;
  if (!el || anyLoading.value) return;
  if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 500) {
    loadMoreAll();
  }
};

// ── Drag / wheel ───────────────────────────────────────────
const handleWheel = (e: WheelEvent) => {
  if (scrollWrapper.value) scrollWrapper.value.scrollLeft += e.deltaY + e.deltaX;
};
const onMouseDown = (e: MouseEvent) => {
  if ((e.target as HTMLElement).closest('.tl-bubble, button, .v-btn')) return;
  isDragging = true; hasDragged = false;
  startX = e.clientX;
  scrollLeftStart = scrollWrapper.value?.scrollLeft ?? 0;
};
const onMouseMove = (e: MouseEvent) => {
  if (!isDragging || !scrollWrapper.value) return;
  const dx = e.clientX - startX;
  if (Math.abs(dx) > 4) hasDragged = true;
  scrollWrapper.value.scrollLeft = scrollLeftStart - dx;
};
const onMouseUp = () => { isDragging = false; };

// ── Expand ─────────────────────────────────────────────────
const toggleExpand = (item: any) => {
  if (hasDragged) return;
  const uri = getUri(item);
  expandedUri.value  = expandedUri.value === uri ? null : uri;
  expandedItem.value = expandedUri.value ? item : null;
  if (expandedItem.value) {
    nextTick(() => expandedPanel.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
  }
};
const closeExpanded = () => { expandedUri.value = null; expandedItem.value = null; };

// ── Initial load ───────────────────────────────────────────
const fetchPosts = async () => {
  loading.value = true;
  referenceMs.value = Date.now();
  seenUris.clear();
  posts.value = [];

  const srcs = await buildSources();
  sources.value = srcs;

  // Kick off all enabled sources concurrently; show timeline after first resolves
  const enabledSrcs = srcs.filter(s => s.enabled);
  const all = enabledSrcs.map(s => fetchSource(s));
  await Promise.race(all).catch(() => {});

  loading.value = false;
  await nextTick();
  scrollToNow();

  // Remaining sources continue in background
  await Promise.allSettled(all);
};

// ── Navigation ─────────────────────────────────────────────
const viewThread        = (post: any) => router.push(`/profile/${post.author.handle}/post/${post.uri.split('/').pop()}`);
const handleDelete      = (post: any) => { posts.value = posts.value.filter(p => getUri(p) !== post.uri); closeExpanded(); };
const handleTagClick    = (tag: string) => router.push(`/t/${tag}`);
const handleViewProfile = (handle?: string) => { if (handle) router.push(`/profile/${handle}`); };

onMounted(fetchPosts);

// Load discovers once currentUser is available (auth resolves after mount)
watch(currentUser, async (user) => {
  if (!user?.did) return;
  const enabled = loadEnabled() ?? new Set(['gamedevid']);
  try {
    const discovers = await getDiscovers();
    for (const d of discovers) {
      const id = `discover-${d.rkey || d.name}`;
      if (sources.value.some(s => s.id === id)) continue; // already present
      const src = {
        id,
        label: d.name,
        query: d.query,
        kind: 'discover' as const,
        done: false,
        loading: false,
        error: false,
        enabled: enabled.has(id),
      };
      sources.value = [...sources.value, src];
      if (src.enabled) fetchSource(src);
    }
  } catch { /* optional */ }
}, { immediate: false });
</script>

<style scoped>
/* ── Toolbar ──────────────────────────────────────────────── */
.tl-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.tl-zoom-group {
  display: flex;
  gap: 2px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 20px;
  padding: 3px;
}

.zoom-btn {
  padding: 3px 10px;
  border-radius: 16px;
  font-size: 0.68rem;
  font-weight: 700;
  color: rgba(255,255,255,0.45);
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
  white-space: nowrap;
}
.zoom-btn:hover { color: rgba(255,255,255,0.8); }
.zoom-btn.active {
  background: rgba(219,168,88,0.18);
  color: rgba(219,168,88,0.95);
}

.tl-toolbar-actions {
  display: flex;
  gap: 6px;
  margin-left: auto;
}

/* ── Source chips ─────────────────────────────────────────── */
.tl-sources {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 7px;
}

.source-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px 2px 6px;
  border-radius: 20px;
  font-size: 0.6rem;
  font-weight: 700;
  border: 1px solid rgba(255,255,255,0.07);
  background: rgba(255,255,255,0.03);
  color: rgba(255,255,255,0.35);
  transition: border-color 0.2s, color 0.2s;
}
.chip-loading {
  border-color: rgba(219,168,88,0.3);
  color: rgba(219,168,88,0.7);
}
.chip-done {
  border-color: rgba(100,200,100,0.2);
  color: rgba(100,200,100,0.55);
}
.chip-error {
  border-color: rgba(255,80,80,0.2);
  color: rgba(255,100,100,0.55);
}
.chip-disabled {
  opacity: 0.35;
  color: rgba(255,255,255,0.2);
}

/* ── Strip ────────────────────────────────────────────────── */
.tl-strip {
  overflow-x: auto;
  overflow-y: hidden;
  height: 400px;
  cursor: grab;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.07);
  background: rgba(255,255,255,0.015);
  scrollbar-width: thin;
  scrollbar-color: rgba(219,168,88,0.4) transparent;
}
.tl-strip:active { cursor: grabbing; }
.tl-strip::-webkit-scrollbar { height: 4px; }
.tl-strip::-webkit-scrollbar-thumb { background: rgba(219,168,88,0.35); border-radius: 2px; }

/* ── Track ────────────────────────────────────────────────── */
.tl-track { position: relative; }

/* ── Day separator lines ──────────────────────────────────── */
.tl-day-sep {
  position: absolute;
  top: 0;
  width: 0;
  border-left: 1px dashed rgba(219,168,88,0.15);
  pointer-events: none;
  z-index: 1;
}

/* ── Floating bubbles ─────────────────────────────────────── */
.tl-bubble {
  position: absolute;
  width: 148px;
  max-height: 62px;
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  padding: 6px 8px 5px;
  cursor: pointer;
  box-sizing: border-box;
  overflow: hidden;
  user-select: none;
  transition: max-height 0.18s ease, border-color 0.15s, background 0.15s, box-shadow 0.2s, z-index 0s;
  z-index: 3;
}
.tl-bubble:hover {
  max-height: 220px;
  border-color: rgba(219,168,88,0.5);
  background: #242424;
  box-shadow: 0 4px 18px rgba(0,0,0,0.8);
  z-index: 30;
  overflow: visible;
}
.tl-bubble.bubble-active {
  border-color: rgba(219,168,88,0.8);
  background: #1e1a0e;
  z-index: 20;
}
.tl-bubble.bubble-active:hover {
  background: #252010;
}

.bubble-header {
  display: flex; align-items: center;
  gap: 6px;
  margin-bottom: 3px;
  min-width: 0;
}
.bubble-name {
  font-size: 0.68rem; font-weight: 800; color: #fff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  min-width: 0;
}
.bubble-text {
  font-size: 0.68rem;
  line-height: 1.4;
  color: rgba(255,255,255,0.65);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── Ruler ────────────────────────────────────────────────── */
.tl-ruler {
  position: absolute;
  left: 0; right: 0;
  height: 40px;
  background: rgba(0,0,0,0.35);
  border-top: 1px solid rgba(219,168,88,0.2);
  user-select: none;
  z-index: 5;
}

.tl-mark {
  position: absolute;
  bottom: 0;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  cursor: pointer;
}
.tl-mark:hover .mark-label { color: rgba(219,168,88,0.9); }

.mark-tick {
  width: 1px;
  height: 6px;
  background: rgba(255,255,255,0.14);
  margin-bottom: 2px;
}
.mark-major .mark-tick { height: 14px; background: rgba(219,168,88,0.55); }
.mark-day   .mark-tick { height: 18px; background: rgba(219,168,88,0.85); }

.mark-label {
  font-size: 0.58rem;
  color: rgba(255,255,255,0.3);
  white-space: nowrap;
  line-height: 1;
  margin-bottom: 2px;
  transition: color 0.15s;
}
.mark-major .mark-label {
  font-size: 0.64rem;
  font-weight: 700;
  color: rgba(219,168,88,0.7);
}
.mark-day .mark-label {
  font-size: 0.64rem;
  font-weight: 800;
  color: rgba(219,168,88,0.95);
}

/* ── "Now" line at x=0 ────────────────────────────────────── */
.tl-now-line {
  position: absolute;
  top: 0; bottom: 0;
  width: 2px;
  background: rgba(255,80,80,0.7);
  pointer-events: none;
  z-index: 6;
}
.now-badge {
  position: absolute;
  top: 3px;
  left: 4px;
  background: rgba(255,80,80,0.85);
  color: #fff;
  font-size: 0.52rem;
  font-weight: 800;
  padding: 1px 5px;
  border-radius: 4px;
  white-space: nowrap;
}

/* ── Hovered post connector ───────────────────────────────── */
.tl-hover-connector {
  position: absolute;
  width: 0;
  border-left: 1.5px dashed rgba(219,168,88,0.55);
  pointer-events: none;
  z-index: 15;
  transform: translateX(-50%);
}

/* ── Hover time mark on ruler ─────────────────────────────── */
.tl-hover-mark {
  position: absolute;
  top: 0;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 8;
}
.hover-label {
  background: #DBA858;
  color: #1a1200;
  font-size: 0.6rem;
  font-weight: 800;
  padding: 2px 7px;
  border-radius: 99px;
  white-space: nowrap;
  box-shadow: 0 0 0 2px #1a1a1a;
}

/* ── Lazy-load spinner ────────────────────────────────────── */
.tl-load-spinner { position: absolute; z-index: 7; opacity: 0.7; }

/* ── Scroll hint + loaded posts ───────────────────────────── */
.scroll-hint {
  display: flex; align-items: center; gap: 4px;
  margin-top: 5px;
  font-size: 0.62rem;
  color: rgba(255,255,255,0.22);
}

.loaded-posts-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 700;
  color: rgba(219,168,88,0.7);
  background: rgba(219,168,88,0.07);
  border: 1px solid rgba(219,168,88,0.22);
  cursor: pointer;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
  white-space: nowrap;
}
.loaded-posts-btn:hover {
  color: rgba(219,168,88,0.95);
  background: rgba(219,168,88,0.13);
  border-color: rgba(219,168,88,0.4);
}

/* ── Feed popup ───────────────────────────────────────────── */
.feed-popup {
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 14px;
  padding: 10px 0 8px;
  min-width: 200px;
  max-width: 240px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.6);
}

.feed-popup-title {
  font-size: 0.65rem;
  font-weight: 800;
  color: rgba(255,255,255,0.4);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0 12px 8px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  margin-bottom: 4px;
}

.feed-popup-list {
  max-height: 280px;
  overflow-y: auto;
  padding: 0 6px;
  scrollbar-width: thin;
  scrollbar-color: rgba(219,168,88,0.3) transparent;
}

.feed-popup-section-label {
  font-size: 0.55rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255,255,255,0.25);
  padding: 4px 6px 2px;
}

.feed-popup-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s;
}
.feed-popup-item:hover { background: rgba(255,255,255,0.05); }

.feed-checkbox {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: 1.5px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s, background 0.15s;
}
.feed-checkbox.checked {
  border-color: rgba(219,168,88,0.7);
  background: rgba(219,168,88,0.12);
}

.feed-popup-label {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.feed-name {
  font-size: 0.68rem;
  font-weight: 700;
  color: rgba(255,255,255,0.75);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.feed-meta {
  font-size: 0.58rem;
  color: rgba(255,255,255,0.3);
  line-height: 1.3;
}

.feed-popup-actions {
  display: flex;
  gap: 6px;
  padding: 8px 12px 0;
  border-top: 1px solid rgba(255,255,255,0.06);
  margin-top: 6px;
}
.feed-popup-action-btn {
  font-size: 0.6rem;
  font-weight: 700;
  color: rgba(219,168,88,0.6);
  background: rgba(219,168,88,0.07);
  border: 1px solid rgba(219,168,88,0.15);
  border-radius: 8px;
  padding: 2px 8px;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
}
.feed-popup-action-btn:hover {
  color: rgba(219,168,88,0.95);
  background: rgba(219,168,88,0.14);
}

/* ── Expanded panel ───────────────────────────────────────── */
.expanded-panel-wrap {
  display: flex;
  justify-content: center;
}
.expanded-panel {
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 16px;
  width: 100%;
  max-width: 640px;
  max-height: 400px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(219,168,88,0.3) transparent;
}

/* ── Transition ───────────────────────────────────────────── */
.slide-up-enter-active, .slide-up-leave-active { transition: opacity 0.22s ease, transform 0.22s ease; }
.slide-up-enter-from, .slide-up-leave-to { opacity: 0; transform: translateY(12px); }
</style>
